import React, { useState, useEffect } from "react";
import { IoCloudDownloadOutline, IoCheckmark, IoAlert } from "react-icons/io5";
import { MdLockPerson } from "react-icons/md"; // Ícone para o botão restrito
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "@hooks/auth"; // Importando o hook de autenticação para verificar o cargo

const DownloadButton = ({
  label,
  endpoint,
  onDownload,
  disabled,
  icon: Icon,
  restricted = false,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    if (isDownloading || disabled) return;

    setIsDownloading(true);
    setError(null);

    try {
      console.log(`Iniciando download de ${endpoint}...`);

      const response = await axios.get(
        `${process.env.REACT_APP_API}/tools/revisor/${endpoint}`,
        {
          headers: {
            Authorization: `Bearer ${onDownload.token}`,
            projeto: onDownload.projectId,
          },
          responseType: "blob", // Importante: define que a resposta é um blob binário
        }
      );

      console.log("Resposta recebida:", response);

      // Verifica o Content-Type da resposta
      const contentType = response.headers["content-type"];
      console.log("Content-Type:", contentType);

      // Se for JSON, provavelmente é uma mensagem de erro
      if (contentType && contentType.includes("application/json")) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            console.error("Erro retornado:", errorData);
            throw new Error(errorData.error || "Erro ao baixar arquivo");
          } catch (e) {
            throw new Error("Erro ao baixar arquivo");
          }
        };
        reader.readAsText(response.data);
        return;
      }

      // Determina a extensão do arquivo com base no Content-Type
      let extension = ".zip";
      if (contentType) {
        if (
          contentType.includes("spreadsheetml") ||
          contentType.includes("openxmlformats") ||
          contentType.includes("excel")
        ) {
          extension = ".xlsx";
        } else if (contentType.includes("zip")) {
          extension = ".zip";
        }
      }

      // Determina o nome do arquivo dependendo do endpoint
      let fileName;
      if (endpoint === "download-ausentes-es") {
        fileName = `Ausentes_ES_${Date.now()}${extension}`;
      } else {
        fileName = `Cruzamento_SPEDs_${endpoint}_${Date.now()}${extension}`;
      }

      // Cria o URL para download
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: contentType || "application/octet-stream",
        })
      );

      // Cria um elemento de link e dispara o download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Download concluído com sucesso!");
    } catch (error) {
      console.error("Erro de download:", error);
      setError(error.message);

      if (error.response && error.response.status === 403) {
        toast.error(
          "Acesso negado. Você não tem permissão para esta operação."
        );
      } else {
        toast.error(error.response?.data?.error || "Erro ao realizar download");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={`btn ${restricted ? "restricted" : ""}`}
      onClick={handleDownload}
      title={
        restricted ? "Acesso restrito a usuários com cargo específico" : ""
      }
    >
      {isDownloading ? (
        <div className="spinner" />
      ) : error ? (
        <IoAlert />
      ) : (
        <Icon />
      )}
      <p>{label}</p>
    </div>
  );
};

const RevisorDownloads = ({ token, projectId, isProcessingComplete }) => {
  const { user } = useAuth(); // Obtendo informações do usuário atual
  const isGodUser = user?.cargo === "god"; // Verificando se o usuário tem cargo "god"

  const downloadProps = {
    token,
    projectId,
  };

  const downloads = [
    {
      label: "BAIXAR RELATÓRIO COMPLETO",
      endpoint: "download",
      icon: IoCloudDownloadOutline,
    },
    {
      label: "BAIXAR XMLs ENCONTRADOS",
      endpoint: "download-xml",
      icon: IoCloudDownloadOutline,
    },
    {
      label: "BAIXAR PLANILHAS EXCEL",
      endpoint: "download-excel",
      icon: IoCloudDownloadOutline,
    },
  ];

  // Adicionar o botão de Ausentes ES apenas para usuários "god"
  const godOnlyDownloads = [
    {
      label: "BAIXAR AUSENTES ES",
      endpoint: "download-ausente",
      icon: MdLockPerson,
      restricted: true,
    },
  ];

  // Combina os downloads normais com os restritos (apenas para usuários god)
  const allDownloads = isGodUser
    ? [...downloads, ...godOnlyDownloads]
    : downloads;

  return (
    <div className="buttons">
      {allDownloads.map((download) => (
        <DownloadButton
          key={download.endpoint}
          label={download.label}
          endpoint={download.endpoint}
          onDownload={downloadProps}
          disabled={!isProcessingComplete}
          icon={download.icon}
          restricted={download.restricted || false}
        />
      ))}
    </div>
  );
};

export default RevisorDownloads;
